import { P2GBox, P2GChip, P2GDivider, P2GLink, P2GText, P2GTooltip } from '@parcel2go/core-ui';
import { QuoteExtraApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SidePanelContent } from '~/components/sidepanel-section';
import ItemCustomsDropDown from './ItemCustomsDropDown';
import { ExtrasToggle } from './ItemExtrasToggle';
import { IossNumber, IossNumberSource } from '~/apps/smartsend/lib/consts/ItemMetaConstants';
import { DeliveredDutyPaidExtra } from '../../ExtrasHelper';
import { RequiresCustoms } from '~/apps/smartsend/lib/helpers/SmartSendValidation';
import { useMemo } from 'react';
import { EditType } from './EditType';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  item: SmartSendItemApiModel;
  wrapper: (content: React.ReactNode) => React.ReactNode;
  ddpExtra?: QuoteExtraApiModel & { Available: boolean; TranslatedName: string; Price: number };
  edit?: (type: EditType) => void;
  isMobile: boolean;
}

export default function ItemCustomsQuickLabel({ item, ddpExtra, wrapper, edit, isMobile }: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const requiresCustomsForItem = RequiresCustoms(item?.State?.validation);

  const ddpCanBeSelected = ddpExtra?.Available;
  const ddpSelected = item.Extras.some((x) => x.Id === DeliveredDutyPaidExtra);

  const iossCanBeAdded = !!item.State?.validation?.QuoteState?.ItemValidation?.some((x) => x.name === IossNumber);
  const iossAdded = !!item.Meta[IossNumber]?.Value;

  const somethingCanBeSelected = ddpCanBeSelected || iossCanBeAdded;

  const selectedOption = useMemo(() => {
    if (ddpSelected && ddpCanBeSelected) {
      return 'DDP';
    }
    if (iossCanBeAdded && iossAdded) {
      return 'IOSS';
    }
    return null;
  }, [ddpSelected, ddpCanBeSelected, iossCanBeAdded, iossAdded]);

  if (!requiresCustomsForItem) {
    return null;
  }

  return wrapper(
    <P2GBox sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
      <P2GBox>
        {isMobile || !somethingCanBeSelected ? (
          <ExtrasToggle
            e={{
              // Pass this ID for the icon
              Id: DeliveredDutyPaidExtra,
              Name: '',
              Available: somethingCanBeSelected
            }}
            selected={!!selectedOption}
            onToggleExtra={() => edit?.('CUSTOMS')}
          />
        ) : (
          <ItemCustomsDropDown
            item={item}
            control={
              <P2GTooltip
                variant="light"
                title={
                  <SidePanelContent>
                    <P2GBox
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'nowrap'
                      }}
                    >
                      <P2GText>
                        <strong>{t('DUTIES_QUICK_ACTION_MENU_TITLE', { defaultValue: 'Duties and Taxes' })}</strong>
                      </P2GText>
                      <P2GBox
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          mb: 1
                        }}
                      >
                        {selectedOption === 'DDP' && <P2GChip color="success" size="small" label="DDP" />}
                        {selectedOption === 'IOSS' && <P2GChip color="success" size="small" label="IOSS" />}
                      </P2GBox>
                    </P2GBox>
                    {selectedOption === null && (
                      <P2GText variant="body2">
                        {t('DUTIES_QUICK_ACTION_SELECT_CUSTOMS_OPTION', {
                          defaultValue: 'Select how you would like customs to be handled for this item.'
                        })}
                      </P2GText>
                    )}
                    {selectedOption === 'DDP' && (
                      <P2GText variant="body2">
                        {t('DUTIES_QUICK_ACTION_DDP_SELECTED', {
                          defaultValue: 'You have opted to use DDP to pay duties and taxes for this item.'
                        })}
                      </P2GText>
                    )}
                    {selectedOption === 'IOSS' &&
                      (item.Meta[IossNumberSource]?.Value === 'Api' ? (
                        <P2GText variant="body2">
                          {t('DUTIES_QUICK_ACTION_IOSS_API', {
                            defaultValue: 'Your duties were paid by the marketplace using their IOSS number.'
                          })}
                        </P2GText>
                      ) : (
                        <P2GText variant="body2">
                          {t('DUTIES_QUICK_ACTION_IOSS_PROVIDED', {
                            defaultValue: `You have provided an IOSS number (${item.Meta[IossNumber]?.Value}) for this item.`
                          })}
                        </P2GText>
                      ))}
                  </SidePanelContent>
                }
              >
                <ExtrasToggle
                  e={{
                    // Pass this ID for the icon
                    Id: DeliveredDutyPaidExtra,
                    Name: '',
                    Available: somethingCanBeSelected
                  }}
                  selected={!!selectedOption}
                />
              </P2GTooltip>
            }
            tooltipContent={
              <P2GBox mb={1}>
                <P2GText>
                  <strong>{t('DUTIES_QUICK_ACTION_MENU_TITLE', { defaultValue: 'Duties and Taxes' })}</strong>
                </P2GText>
              </P2GBox>
            }
            tooltipFooterContent={
              edit && (
                <P2GBox mb={1} mt={2}>
                  <P2GDivider />
                  <P2GText variant="body2" component="div" mt={1}>
                    <Trans
                      t={t}
                      i18nKey="DUTIES_QUICK_ACTION_MENU_INSTRUCTION"
                      defaults="For more information about duties and taxes, <a>click here</a>."
                      components={{ a: <P2GLink onClick={() => edit('CUSTOMS')} /> }}
                    />
                  </P2GText>
                </P2GBox>
              )
            }
          />
        )}
      </P2GBox>
      <P2GBox>
        <P2GText variant="body2">
          {selectedOption ? (
            <strong style={{ color: '#629833' }}>{selectedOption}</strong>
          ) : (
            <strong>{t('DUTIES_QUICK_ACTION_MENU_LABEL', { defaultValue: 'Duties' })}</strong>
          )}
        </P2GText>
      </P2GBox>
    </P2GBox>
  );
}
